import { AfterViewInit, Component, Input, OnInit, OnDestroy, ViewChild, signal, effect, computed } from '@angular/core';
import { filter, firstValueFrom, pairwise, race, Subscription, throwError, timer, switchMap } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { ControlService } from '../library/control.service';
import { RealtimeService } from '../library/realtime/realtime.service';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { PlayerService } from '../library/player.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { IPathwayNextStepResponse, IPathwayRunStepResponse, IPathwayCompleteStepResponse } from 'src/app/interfaces/ipathway-enrollment';
import { IPdfLearnState, IPdfProgress } from 'src/app/interfaces/ipdf-learn-state';
import { IVideoLearnState } from 'src/app/interfaces/ivideo-learn-state';
import { PathwayStepState, PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { AppConstants } from 'src/app/app-constants';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { LoggerService } from 'src/app/services/logger/logger.service';
import {
  AudioCallControlService,
  ConversationState,
} from 'src/app/services/learner/audio-call-control.service';
import { CallPathwayVideoComponent } from './call-flow/call-pathway-video/call-pathway-video.component';
import { PathwaySubtitleService, SubtitleContext } from './pathway-subtitle.service';
import { CallService } from 'src/app/services/learner/call.service';
import { IInitiatePathwayStepSimulationResponse } from 'src/app/interfaces/call/iinitiate-pathway-step-simulation';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';

const DEFAULT_PDF_URL =
  '/assets/pdf/NOV_IMUS_One-BA_Selling_Approach_Sales_Field_Guide_6.23_FINAL_2.pdf';

type DeviceSettingsModalContext = Readonly<{
  wasPausedBeforeOpen: boolean;
  micOnBeforeOpen: boolean;
  videoOnBeforeOpen: boolean;
}>;

@Component({
  host: {
    class: 'theme-dark',
  },
  selector: 'app-call-pathway',
  standalone: false,
  templateUrl: './call-pathway.component.html',
  styleUrls: ['./call-pathway.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'learningPathway',
    },
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'call',
    },
  ],
})
export class CallPathwayComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() controlService: ControlService | undefined;
  @Input() realtimeService: RealtimeService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() callStore: CallStoreService | undefined;
  private readonly _enrollmentStepUuid = signal<string>('');
  private _enrollmentStepUuidReady = false;
  @Input() set enrollmentStepUuid(value: string) { this._enrollmentStepUuid.set(value); }
  get enrollmentStepUuid(): string { return this._enrollmentStepUuid(); }
  @Input() enrollmentUuid: string = '';
  private readonly _stepType = signal<string>(PathwayConstants.STEP_TYPE_INTRO);
  @Input() set stepType(value: string) { this._stepType.set(value); }
  get stepType(): string { return this._stepType(); }
  protected get isStepTypePracticeCall() { return this.stepType === PathwayStepType.PRACTICE_CALL; }

  @ViewChild(CallPathwayVideoComponent) private videoComponent?: CallPathwayVideoComponent;

  private currentStepUuid: string = '';
  private currentCallUuid: string = '';
  private currentCallType: string = '';
  private currentStepState = signal<PathwayStepState | null>(null);
  private isIntroStepCompleted: boolean = false;
  private isAutoCompleted: boolean = false;
  private isStepCompleted: boolean = false;
  public instanceUuid: string = '';
  private moduleAssignmentUuid: string = '';
  public callLearnUuid = signal<string>('');
  private pathwayCompletedOnLastStep: boolean = false;
  private conversationState = signal<ConversationState | null>(null);
  private readonly isVideoCurrentlyPlaying = signal(false);
  private isStepInitializationPending: boolean = false;

  private hasCallStarted(): boolean {
    return (this.realtimeService?.joinedMeeting ?? false) || !!this.conversationState();
  }
  private deviceSettingsModalContext: DeviceSettingsModalContext | null = null;

  constructor(
    private readonly pathwaysService: PathwaysService,
    private readonly callService: CallService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translationService: TranslationService,
    private readonly loggerService: LoggerService,
    private readonly audioCallControlService: AudioCallControlService,
    private readonly pathwaySubtitleService: PathwaySubtitleService
  ) {
    effect(() => {
      const speech = this.playerService?.avatarSpeech?.() || '';
      this.captionText.set(speech);
    });

    effect(() => {
      this.disableConfirmActionButton.set(this.shouldDisableConfirmButton());
    });

    effect(() => {
      const isCallComplete = this.callStore?.getIsCallComplete();
      if (isCallComplete) {
        this.confirmActionLabel.set('learningPathway.VIEW_RESULTS');
      }
    });

    // Switch to quiz-result view once terminateConversation succeeds
    effect(() => {
      if (this.callStore?.getShowQuizResultView()) {
        this.quizResultTtsDone.set(false);
        this.callFlow.set(PathwayConstants.CALL_FLOW_QUIZ_RESULT);
      }
    });

    // Sync enrollmentStepUuid and stepType to URL on every step transition (skip initial @Input values)
    effect(() => {
      const uuid = this._enrollmentStepUuid();
      const stepType = this._stepType();
      if (!this._enrollmentStepUuidReady || !uuid || !stepType) return;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { enrollmentStepUuid: uuid, stepType },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

  }

  // header
  title = signal('');
  isLoading = signal(false);
  leaveStepModalOpen = signal(false);
  leaveStepModalDescription = signal('');

  protected get disableVideoButton(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return true;
    }
    return !this.callStore?.getCall();
  }

  private initQuizGuidedCall(response: IPathwayRunStepResponse): void {
    this.confirmActionLabel.set('learningPathway.SUBMIT_ANSWER');
    this.disableConfirmActionButton.set(true);

    if (!response.callUuid) {
      this.loggerService.error('Missing callUuid in QUIZ IN_PROGRESS step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.currentCallType = response.callType;
    this.enrollmentStepUuid = response.stepUuid;


    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for QUIZ IN_PROGRESS step setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );
      this.controlService.interactionCount = 0;
      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.callStore?.setShowQuizResultView(false);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');

    if (!this.realtimeService) {
      // Fallback to legacy Tavus start if realtimeService is not available
      this.startTavusCall(false);
      this.mainIsLoading.set(false);
      return;
    }

    // Mirror what startCallRequest(doGuidedValidation=true) sets up before fetching guided info
    this.realtimeService.isGuidedCall = true;
    this.realtimeService.isFirstGuidedInteraction = true;
    this.realtimeService.guidedIndex = 0;
    this.callStore?.setIsContinueButtonHidden(false);
    this.callStore?.setIsContinueButtonBlocked(true);

    // When the guided quiz call auto-completes (avatar finishes last question), show results
    this.realtimeService.onAutoComplete = () => {
      this.realtimeService!.onAutoComplete = null;
      // sendRequestToEndCall is normally triggered via control.service on manual submit;
      // in the auto-complete path it must be called explicitly here.
      this.realtimeService!.sendEndCallRequest();
      this.realtimeService!.isGuidedCall = false;
      this.handleQuizCompletionAndShowResults().catch((err) => {
        this.loggerService.error('[PATHWAY] handleQuizCompletionAndShowResults failed', err);
        this.setErrorState(err);
      });
    };

    if (!this.moduleAssignmentUuid) {
      this.setErrorState('Missing moduleAssignmentUuid for QUIZ pathway simulation init');
      return;
    }

    // For QUIZ steps, initialize the practice call and Tavus conversation via
    // initiatePathwayStepSimulation. Guided metadata (questions, hints, visual aids)
    // will still be fetched separately via fetchGuidedEchoInfo.
    this.subscriptions.push(
      this.callService
        .initiatePathwayStepSimulation(
          this.moduleAssignmentUuid,
          this.currentCallUuid,
          PathwayConstants.PRACTICE_TYPE_PATHWAY,
          AppConstants.CALL_TYPE_GUIDED,
          this.enrollmentStepUuid
        )
        .subscribe({
          next: async (res: IInitiatePathwayStepSimulationResponse) => {
            try {
              if (!this.realtimeService) {
                this.setErrorState('Missing realtimeService for QUIZ pathway simulation init');
                return;
              }

              const conversationId = res.conversationId ?? '';
              const conversationUrl = res.conversationUrl ?? '';
              if (!conversationId || !conversationUrl) {
                this.setErrorState('initiatePathwayStepSimulation response missing conversation details for QUIZ step');
                return;
              }

              // Build a minimal ICallRealtime payload from the simulation response
              const realtimePayload: ICallRealtime = {
                practiceCallUuid: res.practiceCallUuid,
                conversationId,
                conversationUrl,
                callName: res.callName ?? '',
                avatarName: res.avatarName ?? this.playerService?.avatarName ?? '',
                pronounceWords: res.pronounceWords ?? [],
                resumeMessage: res.resumeMessage ?? [],
                visualAid: res.visualAid ?? undefined,
                customBackgroundUrl: res.customBackgroundUrl ?? undefined,
                hideResultsForLearners: res.hideResultsForLearners ?? null,
              };

              this.callStore?.setDisplayPostCall(false);
              await this.realtimeService.initializeFromStartConversationResponse(
                realtimePayload,
                true
              );

              this.callStore?.setCurrentView(AppConstants.VIEW_PRACTICE);
              this.realtimeService.conversationInitializedByPathwayFlow = true;

              await this.realtimeService.getFetchGuidedEchoInfo();
              this.mainIsLoading.set(false);
            } catch (error) {
              this.setErrorState(error as Error);
            }
          },
          error: (err) => {
            this.setErrorState(err);
          },
        })
    );
  }

  private shouldDisableConfirmButton(): boolean {
    // Quiz is fully complete (awaiting results)
    if (this.callStore?.getIsCallComplete()) return false;

    // Outside of an active quiz step, no restriction applies
    // const isQuizInProgress =
    //   this.stepType === PathwayStepType.QUIZ &&
    //   this.currentStepState() === PathwayStepState.IN_PROGRESS;
    // if (!isQuizInProgress) return false;

    // Require a transcript before the user can submit
    const isQuizStep = this.stepType === PathwayStepType.QUIZ;
    if (isQuizStep) {
      const hasTranscript =
        (this.callStore?.getSpeechTranscript().length ?? 0) !== 0 ||
        (this.callStore?.getSpeechTranscriptPartial().length ?? 0) !== 0;
      if (!hasTranscript) return true;
    }

    // Keep locked while the avatar is speaking or an answer is processing
    return this.callStore?.getIsContinueButtonBlocked() ?? false;
  }

  protected get disableMicButton(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return true;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      return true;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      // Disable mic while coach is speaking
      return this.audioCallControlService.isAgentSpeaking;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_PDF) {
      return this.audioCallControlService.isAgentSpeaking;
    }

    // Existing behavior for realtime/Tavus flows
    return !!this.realtimeService?.isFirstMessage;
  }

  disableConfirmActionButton = signal(false);
  confirmActionLabel = signal('Next Step');

  // main
  mainIsLoading = signal(false);
  callFlow = signal<'guide' | 'pdf' | 'video' | 'error' | 'quiz-result' | 'role-play'>(PathwayConstants.CALL_FLOW_GUIDE);
  isPreamble = signal(false);
  rolePlayCallUuid = signal<string>('');
  rolePlayCallType = signal<string>('');
  runStepResponse = signal<IPathwayRunStepResponse | null>(null);

  isFirstAttempt = signal<boolean>(true);
  quizOutcome = signal<string>(AppConstants.QUIZ_PASSED_NEXT_STEP);
  quizResultTtsReady = signal<boolean>(false);
  quizStepsToReview = signal<string[]>([]);
  private quizCompletionNextStepResponse: IPathwayNextStepResponse | null = null;

  videoSrc = signal('https://simulation-temporary-test.s3.amazonaws.com/MenquadfiPreCall.mp4');
  videoStartTimeMs = signal<number>(0);
  pdfViewerUrl = signal<string>(DEFAULT_PDF_URL);
  initialPdfPage = signal<number>(1);
  /** Caption text shown in footer (e.g. from originalWelcomeMessage or other APIs). */
  captionText = signal<string>('');
  /** True while quiz-result is playing outcome TTS (so footer shows Speaking + caption). */
  quizResultTtsPlaying = signal<boolean>(false);
  /** True once quiz-result outcome TTS has finished — used to hide avatar and captions. */
  protected quizResultTtsDone = signal<boolean>(false);
  showAvatarPhoto = signal<boolean>(false);
  avatarPhotoUrl = signal<string>('');
  // Error state
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  // Pathway flow state
  private readonly REQUIRED_INTERACTIONS = 0;
  private interactionCheckInterval: any;
  private subscriptions: Subscription[] = [];
  private lastPdfProgress: IPdfProgress | null = null;
  private videoPositionMs: number = 0;
  private videoDurationMs: number = 0;
  private videoProgressInitialized = false;
  private readonly VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL = 10000; // 10s

  ngOnInit(): void {
    console.log('Ahsan');
    this.translationService.loadScopedTranslation('learningPathway');
    this.translationService.loadScopedTranslation('call');

    // Prefer moduleAssignmentUuid from CallStore, fallback to route query param
    this.moduleAssignmentUuid =
      this.callStore?.getModuleAssignmentUuid() || this.route.snapshot.queryParams['moduleAssignmentUuid'] || '';

    if (this.controlService && this.callStore && this.moduleAssignmentUuid) {
      const userUuid = localStorage.getItem('user_uuid') || '';
      this.callStore.setUserUuid(userUuid);
      this.callStore.setModuleAssignmentType(PathwayConstants.PRACTICE_TYPE_PATHWAY);
      this.controlService.isRealTimeCall = true;

      if (this.enrollmentUuid && this.enrollmentStepUuid) {
        this.initializeStep();
      }
    }

    this.subscriptions.push(
      this.audioCallControlService.onCaptionText$.subscribe((text) =>
        this.captionText.set(text)
      )
    );

    this.subscriptions.push(
      this.audioCallControlService.onStateChange$.subscribe((state) => {
        const prevState = this.conversationState();
        this.conversationState.set(state);

        if (
          prevState === ConversationState.AGENT_SPEAKING &&
          state !== ConversationState.AGENT_SPEAKING
        ) {
          this.pathwaySubtitleService.startGracePeriod(this.captionText());
        }

        if (state === ConversationState.USER_SPEAKING) {
          this.pathwaySubtitleService.clearGracePeriod();
        }
      })
    );

    this.subscriptions.push(
      this.audioCallControlService.onError$.subscribe((error) => {
        this.setErrorState(error);
      })
    );

    if (this.realtimeService) {
      this.subscriptions.push(
        this.realtimeService.onError$.subscribe((error) => {
          this.setErrorState(error);
        })
      );
    }

    if (this.deviceSettingsService) {
      this.subscriptions.push(
        this.deviceSettingsService.displayModal$
          .pipe(
            pairwise(),
            filter(([prev, cur]) => prev === true && cur === false)
          )
          .subscribe(() => {
            this.handleDeviceSettingsModalClosed();
          })
      );
    }

    this._enrollmentStepUuidReady = true;
  }

  protected handleMicrophoneClick(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return;
    }
    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      const isMicOn = this.callStore?.getIsMicOn() ?? false;
      if (!isMicOn) {
        // Unmuting during video: pause video first, then open mic
        this.videoComponent?.pauseVideo();
        this.audioCallControlService.setMicMuted(false).catch(console.error);
      } else {
        // Muting during video
        this.audioCallControlService.setMicMuted(true).catch(console.error);
      }
      return;
    }
    this.controlService?.toggleMic();
  }

  protected handleVideoClick(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return;
    }
    if (this.deviceSettingsService?.isCameraUnavailable) {
      this.openDeviceSettingsModal();
    } else {
      this.controlService?.toggleVideo();
    }
  }

  protected handleQuizResultPrimaryAction(): void {
    const outcome = this.quizOutcome();

    if (
      outcome === AppConstants.QUIZ_PASSED_LAST_STEP ||
      outcome === AppConstants.QUIZ_FAILED_LAST_STEP ||
      outcome === AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED
    ) {
      this.navigateToPathwayHome();
      return;
    }

    if (this.quizCompletionNextStepResponse?.toStepUuid && this.quizCompletionNextStepResponse.stepType) {
      this.startStep(this.quizCompletionNextStepResponse);
      return;
    }

    // Fallback: refetch and proceed with normal navigation.
    this.getNextStep();
  }

  /**
   * Footer confirm button label. When in quiz-result flow, returns outcome-specific label.
   */
  protected getConfirmActionLabel(): string {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      const outcome = this.quizOutcome();
      switch (outcome) {
        case AppConstants.QUIZ_PASSED_NEXT_STEP:
          return 'learningPathway.NEXT_STEP';
        case AppConstants.QUIZ_PASSED_LAST_STEP:
          return 'learningPathway.RETURN_TO_PATHWAY';
        case AppConstants.QUIZ_FAILED_PREVIOUS_STEP:
        case AppConstants.QUIZ_FAILED_LAST_STEP:
          return 'learningPathway.TRY_AGAIN';
        case AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED:
          return 'learningPathway.RETURN_TO_PATHWAY';
        default:
          return 'learningPathway.NEXT_STEP';
      }
    }
    return this.confirmActionLabel();
  }

  protected async handleOnConfirmActionClick(): Promise<void> {
    const existTranscript =
        (this.callStore?.getSpeechTranscript().length ?? 0) > 0 ||
        (this.callStore?.getRecognizedTranscripts().length ?? 0) > 0 ||
        (this.callStore?.getSpeechTranscriptPartial().length ?? 0) > 0;
    const isQuiz = this.stepType === PathwayStepType.QUIZ;
    const isCallComplete = this.callStore?.getIsCallComplete();
    const hasNextQuestion = this.realtimeService?.hasNextQuestion();

    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      console.log('[PATHWAY] → blocked: callFlow is error');
      return;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      this.handleQuizResultPrimaryAction();
      return;
    }

    if (isQuiz && existTranscript && !isCallComplete) {
      this.realtimeService?.handleClickContinue().catch((err) => {
        this.loggerService.error('handleClickContinue failed', err);
      });
      return;
    }

    if (isQuiz && !isCallComplete && !this.isPreamble()) {
      return;
    }

    if (isQuiz && isCallComplete) {
      if (!hasNextQuestion) {
        this.handleQuizCompletionAndShowResults().catch((err) => {
          this.loggerService.error('handleQuizCompletionAndShowResults failed', err);
          this.setErrorState(err);
        });
        return;
      }
      console.log('[PATHWAY] → completeStepAndNavigateToNext (quiz complete, next question exists)');
      if (this.enrollmentUuid) {
        this.completeStepAndNavigateToNext();
      }
      return;
    }

    const isLearnStep =
      this.callFlow() === PathwayConstants.CALL_FLOW_PDF ||
      this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO;
    if (isLearnStep) {
      await this.audioCallControlService.endCall().catch((err) => {
        this.loggerService.error('Audio call end failed', err);
      });
      const practiceCallUuid = this.callStore?.getPracticeCallUuid();
      if (practiceCallUuid) {
        this.callService.endLearnCall(practiceCallUuid).subscribe({
          error: (err: any) =>
            this.loggerService.error('endLearnCall failed', err),
        });
      }
    }
    if (this.isPreamble()) {
      if (this.isAutoCompleted) {
        // Preamble: if welcome message was spoken, skip complete API and go directly to main step after preamble
        this.runCurrentStepAfterPreamble();
      } else {
        // Manual preamble confirm path: complete preamble first, then continue flow.
        this.completeStepAndNavigateToNext();
      }
    } else if (this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
      if (this.isIntroStepCompleted) {
        // Intro: if welcome message was spoken, skip complete API and go directly to getNextStep
        if (this.isAutoCompleted) {
          this.getNextStep();
        } else {
          this.completeStepAndNavigateToNext();
        }
      }
    } else {
      // For all other steps: check if step has been completed (e.g., video ended, PDF last page reached)
      if (this.isStepCompleted) {
        if (this.pathwayCompletedOnLastStep) {
          this.pathwayCompletedOnLastStep = false;
          this.navigateToPathwayHome();
        } else {
          this.getNextStep();
        }
      } else {
        this.completeStepAndNavigateToNext();
      }
    }
  }

  private async handleQuizCompletionAndShowResults(): Promise<void> {
    this.mainIsLoading.set(true);
    this.disableConfirmActionButton.set(true);

    // Step 1: Synchronous cleanup of Daily/Tavus call resources
    this.realtimeService?.cleanupCallResources();

    // Step 2: TERMINATE — wait for backend terminateConversation to finish
    try {
      await this.realtimeService?.terminateConversationAsync();
    } catch (error) {
      this.loggerService.error('terminateConversation failed on quiz completion', error);
    }

    // Step 3: SHOW RESULTS — render the quiz-result view and start result data polling
    this.callStore?.setIsMicOn(false);
    this.callStore?.setQuizResultReady(false);
    this.callStore?.setShowQuizResultView(true);
    this.mainIsLoading.set(false);

    try {
      // Step 4: WAIT FOR QUIZ DATA — blocks until getPathwayQuizResultData returns in ResultsGuidedComponent
      await this.waitForQuizResultData();

      // Step 5: FETCH OUTCOME — wait for getNextStep API to resolve
      await this.fetchQuizCompletionOutcomeAsync();
    } catch (error) {
      this.loggerService.error('[PATHWAY] Quiz results could not be loaded, redirecting to pathway home', error);
      this.navigateToPathwayHome();
      return;
    }

    this.disableConfirmActionButton.set(false);
    // Step 6: PLAY TTS — outcome is now known, trigger TTS in quiz-result component
    this.audioCallControlService.skipListenCycleOnNextSpeakEnd();
    this.quizResultTtsReady.set(true);
  }

  private waitForQuizResultData(): Promise<void> {
    if (this.callStore?.getQuizResultReady()) {
      return Promise.resolve();
    }
    return firstValueFrom(
      race(
        this.callStore!.onQuizResultDataReady$(),
        this.callStore!.onQuizResultDataError$().pipe(
          switchMap(() => throwError(() => new Error('[PATHWAY] Quiz result polling timed out')))
        )
      )
    );
  }

  private async fetchQuizCompletionOutcomeAsync(): Promise<void> {
    if (!this.enrollmentUuid) {
      this.loggerService.error('Missing enrollmentUuid for fetchQuizCompletionOutcomeAsync');
      return;
    }

    try {
      const response = await firstValueFrom(
        timer(3000).pipe(
          switchMap(() =>
            this.pathwaysService.getNextStepByEnrollmentUuid(this.enrollmentUuid)
          )
        )
      );

      this.quizCompletionNextStepResponse = response;
      this.quizStepsToReview.set(response.stepsToReview ?? []);

      if (
        response.status === PathwayConstants.API_STATUS_BLOCKED &&
        response.reason === PathwayConstants.REASON_ENROLLMENT_LOCKED
      ) {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED);
        return;
      }

      const hasToStep = !!response.toStepUuid;
      const reason = response.reason;

      // Mapping provided by backend:
      // - toStepUuid has value + reason NEXT_STEP_AVAILABLE → passed, move to next step
      // - toStepUuid has value + reason COMPLETION_REQUIREMENT_FAILED → failed, move to failure target
      // - toStepUuid empty + reason PATHWAY_COMPLETED → final quiz passed
      // - toStepUuid empty + reason COMPLETION_REQUIREMENT_FAILED → final quiz failed
      // - toStepUuid empty + reason MAX_ATTEMPT_REACHED → max attempts reached
      if (hasToStep && reason === 'NEXT_STEP_AVAILABLE') {
        this.quizOutcome.set(AppConstants.QUIZ_PASSED_NEXT_STEP);
        this.disableConfirmActionButton.set(false);
      } else if (hasToStep && reason === 'COMPLETION_REQUIREMENT_FAILED') {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_PREVIOUS_STEP);
      } else if (!hasToStep && reason === 'PATHWAY_COMPLETED') {
        this.quizOutcome.set(AppConstants.QUIZ_PASSED_LAST_STEP);
      } else if (!hasToStep && reason === 'COMPLETION_REQUIREMENT_FAILED') {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_LAST_STEP);
      } else if (!hasToStep && reason === 'MAX_ATTEMPT_REACHED') {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED);
      }
    } catch (error) {
      this.loggerService.error('fetchQuizCompletionOutcomeAsync failed', error);
      throw error;
    }
  }

  protected handleSettingsClick(): void {
    this.openDeviceSettingsModal();
  }

  private openDeviceSettingsModal(): void {
    if (!this.deviceSettingsService) return;
    this.deviceSettingsModalContext = {
      wasPausedBeforeOpen: this.callStore?.getIsPaused() ?? false,
      micOnBeforeOpen: this.callStore?.getIsMicOn() ?? false,
      videoOnBeforeOpen: this.controlService?.isVideoOn ?? false,
    };
    this.deviceSettingsService.displayDeviceSettingsModal();
  }

  private handleDeviceSettingsModalClosed(): void {
    const ctx = this.deviceSettingsModalContext;
    // Only act if this component initiated the modal.
    if (!ctx) return;
    this.deviceSettingsModalContext = null;

    if (this.callStore?.getIsAudioCallMode()) {
      return;
    }

    if (ctx.wasPausedBeforeOpen) return;

    const isPaused = this.callStore?.getIsPaused() ?? false;
    if (!isPaused) return;

    if (this.controlService) {
      this.controlService.isMicOnBeforePause = ctx.micOnBeforeOpen;
      this.controlService.isVideoOnBeforePause = ctx.videoOnBeforeOpen;
    }

    if (this.controlService?.isRealTimeCall) {
      this.realtimeService?.unpauseCall();
      return;
    }
    this.controlService?.unpauseCall();
  }

  protected handleLeaveStepClick(): void {
    // Only show modal for QUIZ step
    if (this.stepType === PathwayStepType.QUIZ && !this.isPreamble() && this.callFlow() !== PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      this.leaveStepModalDescription.set(this.translationService.translate('EXITING_QUIZ_EARLY', 'learningPathway'));
      this.leaveStepModalOpen.set(true);
    } else {
      // For non-quiz steps, exit immediately
      this.endRealtimeCall();
      this.executeLeaveStep();
    }
  }

  protected handleLeaveStepConfirm(): void {
    this.leaveStepModalOpen.set(false);
    this.executeLeaveStep();
  }

  private executeLeaveStep(): void {
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    const isQuizStep = this.stepType === PathwayStepType.QUIZ;
    const isQuizResultFlow = this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT;
    if (isQuizStep || isQuizResultFlow) {
      this.resetCallState();
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_PDF) {
      this.savePdfProgressAndLeaveStep();
    } else if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      this.saveVideoProgressAndLeaveStep();
    } else {
      this.navigateAfterLeaveStep();
    }
  }

  private resetCallState(): void {
    this.audioCallControlService.endCall().catch((err) => {
      this.loggerService.error('audioCallControlService endCall failed on reset', err);
    });
    this.callStore?.setSpeechTranscript('');
    this.callStore?.setIsCallComplete(false);
    this.callStore?.setQuizResultReady(false);
    this.callStore?.setShowQuizResultView(false);
    this.callStore?.setDisplayPostCall(false);
    this.callStore?.setCurrentView('');
    this.callStore?.setCallType('');
    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.quizResultTtsReady.set(false);
    this.quizStepsToReview.set([]);
  }

  private endRealtimeCall(): void {
    if (!this.realtimeService) return;
    try {
      this.realtimeService.endRealtimeConversation(true, true);
    } catch (error) {
      this.loggerService.error('[CALL] tavus - Error ending realtime conversation:', error);
    }
  }

  private savePdfProgressAndLeaveStep(): void {
    this.navigateAfterLeaveStep();

    const progress = this.lastPdfProgress;
    const isOnLastPage = progress && progress.total_pages > 0 && progress.page >= progress.total_pages;
    if (progress && this.instanceUuid && !isOnLastPage) {
      const learnState: IPdfLearnState = {
        type: 'pdf',
        page: progress.page,
        total_pages: progress.total_pages,
      };
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe();
    }
  }

  private navigateAfterLeaveStep(): void {
    if (this.moduleAssignmentUuid) {
      this.navigateToPathwayHome();
    } else {
      console.error(
        'Missing moduleAssignmentUuid in route params for leave step redirect'
      );
    }
  }

  protected onPdfProgressChange(event: IPdfProgress): void {
    const isPageChange =
      this.lastPdfProgress !== null &&
      (event.page !== this.lastPdfProgress.page || event.total_pages !== this.lastPdfProgress.total_pages);
    this.lastPdfProgress = { page: event.page, total_pages: event.total_pages };
    if (!isPageChange || !this.instanceUuid) return;
    const learnState: IPdfLearnState = {
      type: 'pdf',
      page: event.page,
      total_pages: event.total_pages,
    };
    this.subscriptions.push(
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe()
    );
  }

  protected handleLeaveStepResume(): void {
    this.leaveStepModalOpen.set(false);
  }

  protected handleVideoEnded(): void {
    this.isVideoCurrentlyPlaying.set(false);
    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      this.markStepCompleted();
    }
  }

  protected handlePdfCompleted(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_PDF) {
      this.markStepCompleted();
    }
  }

  private markStepCompleted(): void {
    this.isStepCompleted = true;
    this.completeStepOnly();
    if (!this.pathwayCompletedOnLastStep) {
      this.disableConfirmActionButton.set(false);
    }
  }

  private resetStepCompleted(): void {
    this.isStepCompleted = false;
  }

  private setErrorState(error: Error | string): void {
    const errorMessage = error instanceof Error ? error.message : error;
    const previousFlow = this.callFlow();
    this.errorMessage.set(errorMessage);
    this.hasError.set(true);
    this.callFlow.set(PathwayConstants.CALL_FLOW_ERROR);
    this.disableConfirmActionButton.set(true);
    this.mainIsLoading.set(false);

    this.loggerService.error('Call pathway error:', error, {
      previousFlow,
      nextFlow: PathwayConstants.CALL_FLOW_ERROR,
      stepType: this.stepType,
      enrollmentUuid: this.enrollmentUuid,
      enrollmentStepUuid: this.enrollmentStepUuid,
      callUuid: this.callStore?.getCallUuid() ?? '',
      practiceCallUuid: this.callStore?.getPracticeCallUuid() ?? '',
    });
  }

  private clearErrorState(): void {
    this.hasError.set(false);
    this.errorMessage.set('');
  }

  protected getCameraAvailable(): boolean {
    return !this.deviceSettingsService?.isCameraUnavailable;
  }

  protected shouldShowVideoControl(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) return false;
    const isQuizStep = this.stepType === PathwayStepType.QUIZ;
    return (this.stepType === PathwayConstants.STEP_TYPE_INTRO && !this.isPreamble()) || isQuizStep;
  }

  protected shouldShowMicControl(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) return false;
    return true;
  }

  protected onQuizResultSubtitle(text: string | null): void {
    this.captionText.set(text ?? '');
  }

  protected onQuizResultTtsPlaying(playing: boolean): void {
    this.quizResultTtsPlaying.set(playing);
    if (playing) {
      this.quizResultTtsDone.set(false);
    } else {
      this.quizResultTtsDone.set(true);
      this.showAvatarPhoto.set(false);
    }
  }

  protected onQuizResultAvatarThumbnail(url: string): void {
    if (url) {
      this.avatarPhotoUrl.set(url);
      this.showAvatarPhoto.set(true);
    }
  }

  protected showDeviceSettingsScreen(): boolean {
    const isDeviceSettingsView =
      this.callStore?.getCurrentView() === AppConstants.VIEW_DEVICE_SETTINGS;
    const isModalOpen = !!this.deviceSettingsService?.displayModal;
    return !!this.isStepInitializationPending && isDeviceSettingsView && !isModalOpen;
  }

  readonly subtitle = computed(() =>
    this.pathwaySubtitleService.getSubtitle(this.buildSubtitleContext())
  );

  private buildSubtitleContext(): SubtitleContext {
    return {
      callFlow: this.callFlow(),
      stepType: this.stepType,
      conversationState: this.conversationState(),
      isAvatarSpeaking: this.callStore?.getIsAvatarSpeaking() ?? false,
      isVideoPlaying: this.isVideoCurrentlyPlaying(),
      isMicOn: this.callStore?.getIsMicOn() ?? true,
      callStarted: this.hasCallStarted(),
      isAvatarThinking: this.callStore?.getIsAvatarThinking() ?? false,
      quizResultTtsPlaying: this.quizResultTtsPlaying(),
      isPreamble: this.isPreamble(),
      captionText: this.captionText(),
    };
  }

ngAfterViewInit(): void {
    if (this.deviceSettingsService) {
      this.deviceSettingsService.startAudioLevelDetection();
    }

    if (this.playerService) {
      this.callStore?.setIsAvatarSpeaking(false);
    }

    if (this.currentCallUuid) {
      this.startIntroStepInteractionMonitoring();
    }
  }

  private startTavusCall(isFraming: boolean = false): void {
    if (!this.realtimeService) {
      console.error('Missing realtimeService');
      return;
    }

    if (!this.currentCallUuid) {
      console.error('Missing callUuid from API response');
      return;
    }

    if (!this.currentCallType) {
      console.error('Missing callType from API response');
      return;
    }

    const moduleAssignmentUuid = this.callStore?.getModuleAssignmentUuid();
    if (!moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid');
      return;
    }

    if (!this.controlService) {
      console.error('Missing controlService');
      return;
    }

    if (this.controlService.callUuid !== this.currentCallUuid) {
      console.warn('Call UUID mismatch, updating controlService:', {
        controlService: this.controlService.callUuid,
        apiResponse: this.currentCallUuid,
      });
    }

    console.log('Starting Tavus call with API response values:', {
      callUuid: this.currentCallUuid,
      moduleAssignmentUuid: moduleAssignmentUuid,
      callType: this.currentCallType,
      practiceType: PathwayConstants.PRACTICE_TYPE_PATHWAY,
    });

    if (!this.enrollmentStepUuid) {
      console.error('Missing enrollmentStepUuid for framing call');
      return;
    }

    try {
      if (isFraming) {
        this.realtimeService.startFramingCallRequest(this.enrollmentStepUuid).catch((error) => {
          this.setErrorState(error);
        });
      } else {
        this.realtimeService.startCallRequest(false).catch((error) => {
          this.setErrorState(error);
        });
      }
    } catch (error) {
      this.setErrorState(error as Error);
    }
  }

  private startIntroStepInteractionMonitoring(): void {
    if (this.currentCallUuid && this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
      this.isPreamble.set(false);
      this.confirmActionLabel.set('learningPathway.NEXT_STEP');
      this.isIntroStepCompleted = false;

      // Pathway calls are always realtime (Tavus calls)
      if (this.realtimeService) {
        this.disableConfirmActionButton.set(true);
        this.interactionCheckInterval = setInterval(() => {
          if (this.isPreamble()) {
            clearInterval(this.interactionCheckInterval);
            this.interactionCheckInterval = null;
            return;
          }

          const count = this.realtimeService?.avatarInteractionCount || 0;
          const hasEnoughInteractions = count >= this.REQUIRED_INTERACTIONS;

          if (this.currentCallType === AppConstants.CALL_TYPE_FRAMING) {
            this.disableConfirmActionButton.set(true);
            return;
          }

          if (hasEnoughInteractions) {
            // INTRO step is now completed (required interactions met)
            this.isIntroStepCompleted = true;
            this.disableConfirmActionButton.set(false);
            clearInterval(this.interactionCheckInterval);
            this.interactionCheckInterval = null;
          } else {
            this.disableConfirmActionButton.set(true);
          }
        }, PathwayConstants.POLLING_INTERVAL_MS);
      }
    }
  }

  private initializeStep(): void {
    if (!this.enrollmentUuid || !this.enrollmentStepUuid) {
      console.error('Missing enrollmentUuid or enrollmentStepUuid for step initialization');
      return;
    }

    this.clearErrorState();
    this.title.set('');
    this.mainIsLoading.set(true);

    if (this.deviceSettingsService) {
      this.deviceSettingsService.validateDeviceAccess();

      const audioDevice = this.deviceSettingsService.getAudioDeviceLocalStorage() || '';
      const isDeviceSaved = audioDevice.length > 0;

      if (!isDeviceSaved) {
        this.isStepInitializationPending = true;
        this.subscriptions.push(
          this.deviceSettingsService.onDeviceSettingsConfirmed$.subscribe(() => {
            if (this.isStepInitializationPending) {
              this.isStepInitializationPending = false;
              this.title.set('');
              this.mainIsLoading.set(true);
              this.callStore?.setCurrentView('');

              this.proceedWithStepInitialization();
            }
          })
        );
        return;
      }
    }

    this.proceedWithStepInitialization();
  }

  private proceedWithStepInitialization(): void {
    if (!this.enrollmentUuid || !this.enrollmentStepUuid) {
      console.error('Missing enrollmentUuid or enrollmentStepUuid for step initialization');
      return;
    }

    this.avatarPhotoUrl.set('');
    this.showAvatarPhoto.set(false);
    this.subscriptions.push(
      this.pathwaysService.runStepByEnrollmentUuid(this.enrollmentUuid, this.enrollmentStepUuid).subscribe({
        next: (response: IPathwayRunStepResponse) => {
          console.log('runStepByEnrollmentUuid response:', response);

          if (response.status === PathwayConstants.API_STATUS_STARTED) {
            this.instanceUuid = response.instanceUuid ?? '';
            if (this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
              this.setupIntroStep(response);
            } else {
              if (response.isPreamble) {
                // Show preamble call
                this.setupPreambleStep(response);
              } else {
                // Normal step flow
                this.setupNormalStep(response);
              }
            }
          } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
            // Step execution is blocked
            console.error('Step execution blocked:', {
              reason: response.reason,
              message: response.message
            });

            if (response.reason === PathwayConstants.REASON_ANOTHER_STEP_ACTIVE) {
              this.getNextStep();
            } else {
              const errorMessage = response.message || `Step execution blocked: ${response.reason || 'Unknown reason'}`;
              this.setErrorState(errorMessage);
            }
          } else if (response.status === PathwayConstants.API_STATUS_ERROR) {
            // Error running step
            const errorMessage = response.message || `Error running step: ${response.reason || 'Unknown error'}`;
            this.setErrorState(errorMessage);
          } else {
            const errorMessage = `Unexpected runStep response status: ${response.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private setupIntroStep(response: IPathwayRunStepResponse): void {
    this.currentStepUuid = response.stepUuid || this.enrollmentStepUuid;

    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');
    this.isPreamble.set(false);
    this.confirmActionLabel.set('learningPathway.NEXT_STEP');

    if (!response.callUuid) {
      console.error('Missing callUuid in INTRO step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.enrollmentStepUuid = response.stepUuid;

    this.currentCallType = AppConstants.CALL_TYPE_FRAMING;

    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for INTRO step setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );

      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    // Set title: prefer stepName, fall back to callName
    if (response.stepName) {
      this.title.set(response.stepName);
    } else if (response.callName) {
      this.title.set(response.callName);
    }

    // Start interaction monitoring for INTRO step
    this.startIntroStepInteractionMonitoring();

    // Start Tavus call for INTRO
    this.startTavusCall(true);

    // Subscribe to welcome message completion for auto-completion
    this.isAutoCompleted = false;
    if (this.realtimeService) {
      this.subscriptions.push(
        this.realtimeService.welcomeMessageSpoken$.subscribe(() => {
          if (
            !this.isAutoCompleted &&
            this.stepType === PathwayConstants.STEP_TYPE_INTRO
          ) {
            this.isAutoCompleted = true;
            this.isIntroStepCompleted = true;
            if (this.interactionCheckInterval) {
              clearInterval(this.interactionCheckInterval);
              this.interactionCheckInterval = null;
            }
            this.completeStepOnly();
            this.disableConfirmActionButton.set(false);
          }
        })
      );
    }

    this.mainIsLoading.set(false);
  }

  private setupNormalStep(response: IPathwayRunStepResponse): void {
    this.currentStepUuid = response.stepUuid || this.enrollmentStepUuid;
    this.resetStepCompleted();
    this.isFirstAttempt.set(response.stepState !== PathwayStepState.COMPLETED);
    this.currentStepState.set(response.stepState ?? null);

    // Set title: prefer stepName, fall back to callName
    if (response.stepName) {
      this.title.set(response.stepName);
    } else if (response.callName) {
      this.title.set(response.callName);
    }

    this.isPreamble.set(false);
    this.confirmActionLabel.set('learningPathway.NEXT_STEP');
    this.disableConfirmActionButton.set(false);

    // Determine step flow based on step type
    // Note: runStep response doesn't include step type, so we use the component's stepType
    // QUIZ step in progress: handle as guided call
    if (this.stepType === PathwayStepType.QUIZ) {
      this.initQuizGuidedCall(response);
      return;
    }

    // this is a role-play step
    if (this.stepType === PathwayStepType.PRACTICE_CALL) {
      if (!response.callUuid) {
        this.loggerService.error('Missing callUuid in role-play step run response');
        this.mainIsLoading.set(false);
        return;
      }
      this.rolePlayCallUuid.set(response.callUuid);
      this.rolePlayCallType.set(response.callType ?? '');
      this.runStepResponse.set(response);
      this.enrollmentStepUuid = response.stepUuid;

      this.callFlow.set('role-play');
      this.mainIsLoading.set(false);
      return;
    }

    if (this.stepType === PathwayStepType.LEARN_WITH_X) {
      if (!response.callUuid) {
        this.loggerService.error('Missing callUuid in LEARN_WITH_X step run response');
        this.mainIsLoading.set(false);
        return;
      }

      this.currentCallUuid = response.callUuid;
      this.currentCallType = response.callType ?? PathwayStepType.LEARN_WITH_X;
      this.enrollmentStepUuid = response.stepUuid;

      if(this.isFirstAttempt()){
        this.disableConfirmActionButton.set(true);
      } else {
        this.disableConfirmActionButton.set(false);
      }
      this.showAvatarPhoto.set(true);

      this.initiatePathwayStepSimulationForLearnWithX(response);
      return;
    }

    // Default flow for non-LEARN_WITH_X steps is guide
    if (!response.callUuid) {
      console.error('Missing callUuid in normal step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.currentCallType = response.callType;
    this.enrollmentStepUuid = response.stepUuid;


    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for normal step setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );

      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');
    this.startTavusCall(false);
    this.mainIsLoading.set(false);
  }

  private initiatePathwayStepSimulationForLearnWithX(
    runStepResponse: IPathwayRunStepResponse
  ): void {
    if (
      !this.moduleAssignmentUuid ||
      !this.currentCallUuid ||
      !this.enrollmentStepUuid
    ) {
      this.loggerService.error(
        'Missing moduleAssignmentUuid, currentCallUuid or enrollmentStepUuid for initiatePathwayStepSimulation'
      );
      this.mainIsLoading.set(false);
      return;
    }
    this.subscriptions.push(
      this.callService
        .initiatePathwayStepSimulation(
          this.moduleAssignmentUuid,
          this.currentCallUuid,
          PathwayConstants.PRACTICE_TYPE_PATHWAY,
          PathwayStepType.LEARN,
          this.enrollmentStepUuid
        )
        .subscribe({
          next: (res) => {
            this.callStore?.setPracticeCallUuid(res.practiceCallUuid);
            this.callStore?.setCallUuid(this.currentCallUuid);
            this.captionText.set(res.originalWelcomeMessage ?? '');
            this.audioCallControlService.setWelcomeMessage(
              res.pronunciationCorrectedWelcomeMessage ??
                res.originalWelcomeMessage ??
                ''
            );
            this.avatarPhotoUrl.set(res.avatarThumbnail ?? '');
            this.callStore?.setCallWords(res.callWords ?? []);
            this.callStore?.setReplaceWords(res.replaceWords ?? []);

            // Support both array and single-record shapes defensively
            const records = res.callLearnRecords ?? (
              res.callLearn ? [res.callLearn] : []
            );

            if (!records || records.length === 0) {
              this.setErrorState('initiatePathwayStepSimulation returned no callLearn records');
              return;
            }

            const primaryRecord = records[0];
            this.callLearnUuid.set(primaryRecord.uuid);

            if (!primaryRecord.contentLocation) {
              this.setErrorState('initiatePathwayStepSimulation primary record missing contentLocation');
              return;
            }

            if (primaryRecord.contentType === 'VIDEO') {
              this.callFlow.set(PathwayConstants.CALL_FLOW_VIDEO);
              this.videoSrc.set(primaryRecord.contentLocation);
              this.videoStartTimeMs.set(this.getInitialVideoPositionFromResponse(runStepResponse));
              this.resetVideoProgress();
            } else {
              // Default to PDF when contentType is PDF or unknown
              this.callFlow.set(PathwayConstants.CALL_FLOW_PDF);
              this.pdfViewerUrl.set(primaryRecord.contentLocation);

              // PDF-specific state
              this.lastPdfProgress = null;
              this.initialPdfPage.set(
                this.getInitialPdfPageFromResponse(runStepResponse)
              );
            }
            this.callStore?.setCurrentView(AppConstants.VIEW_PRACTICE);
            this.callStore?.setDisplayPostCall(false);
            this.mainIsLoading.set(false);
          },
          error: (err) => {
            this.setErrorState(err);
          },
        })
    );
  }

  private getInitialPdfPageFromResponse(response: IPathwayRunStepResponse): number {
    console.log('getInitialPdfPageFromResponse response:', response);
    const progress = response.instanceProgress;
    if (!progress || progress.type !== 'pdf' || typeof progress.page !== 'number' || progress.page < 1) {
      return 1;
    }
    console.log('Initial PDF page:', progress.page);
    return progress.page;
  }

  private completeStepOnly(): void {
    if (this.isStepTypePracticeCall) {
      this.navigateToPathwayHome();
      return;
    }

    if (!this.enrollmentUuid) {
      console.error('Missing enrollmentUuid for completeStepOnly');
      return;
    }

    this.subscriptions.push(
      this.pathwaysService
        .completeStepByEnrollmentUuid(this.enrollmentUuid)
        .subscribe({
          next: (response: IPathwayCompleteStepResponse) => {
            console.log('[PATHWAY] Step completed via completeStepOnly', response);
            const isPathwayCompleted =
              response.status === PathwayConstants.API_STATUS_COMPLETED &&
              response.reason === PathwayConstants.REASON_PATHWAY_COMPLETED;
            if (isPathwayCompleted) {
              this.pathwayCompletedOnLastStep = true;
              this.confirmActionLabel.set('learningPathway.COMPLETE_STEP');
            }
            this.disableConfirmActionButton.set(false);
          },
          error: (error) => {
            console.error('Error completing step:', error);
          },
        })
    );
  }

  private completeStepAndNavigateToNext(): void {
    // Show loading immediately (hides previous step)
    this.clearErrorState();
    if (!this.isPreamble()) {
      this.title.set('');
    }
    this.mainIsLoading.set(true);

    // Clean up interaction monitoring
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    if (this.realtimeService) {
      try {
        this.realtimeService.endRealtimeConversation(true, true);
      } catch (error) {
        this.setErrorState(error as Error);
      }
    }

    if (this.isStepTypePracticeCall) {
      this.navigateToPathwayHome();
      return;
    }

    // Complete current step
    if (this.enrollmentUuid) {
      this.subscriptions.push(
        this.pathwaysService.completeStepByEnrollmentUuid(this.enrollmentUuid).subscribe({
          next: (response: IPathwayCompleteStepResponse) => {
            if (this.stepType === PathwayStepType.QUIZ) {
              return;
            }
            // Handle different completion statuses
            if (response.status === PathwayConstants.API_STATUS_PREAMBLE_COMPLETED) {
              // Preamble completed, now run the main step
              // We need to run the step again to get the main step instance
              if (response.completedStepUuid) {
                this.runStepAfterPreamble(response.completedStepUuid);
              } else {
                console.error('Missing completedStepUuid in PREAMBLE_COMPLETED response');
                this.mainIsLoading.set(false);
              }
            } else if (response.status === PathwayConstants.API_STATUS_COMPLETED && response.reason === PathwayConstants.REASON_PATHWAY_COMPLETED) {
              this.navigateToPathwayHome();
            } else if (response.status === PathwayConstants.API_STATUS_COMPLETED) {
              // Step completed successfully, get the next step
              // The getNextStep API will determine if there are more steps or if pathway is completed
              this.getNextStep();
            } else if (response.status === PathwayConstants.API_STATUS_REQUIREMENTS_NOT_MET) {
              // Step finished but requirements not met
              const errorMessage = response.message || `Step requirements not met: ${response.requirementFailureReason || 'Unknown reason'}`;
              this.setErrorState(errorMessage);
            } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
                if (response.reason === PathwayConstants.REASON_STEP_ALREADY_COMPLETED) {
                  this.getNextStep();
                } else {
                  // Step completion is blocked
                  const errorMessage = response.message || `Step completion blocked: ${response.reason || 'Unknown reason'}`;
                  this.setErrorState(errorMessage);
                }
            } else if (response.status === PathwayConstants.API_STATUS_ERROR) {
              // Error completing step
              const errorMessage = response.message || `Error completing step: ${response.reason || 'Unknown error'}`;
              this.setErrorState(errorMessage);
            } else {
              // Unexpected status, log and try to get next step
              const errorMessage = `Unexpected complete step response status: ${response.status}`;
              this.setErrorState(errorMessage);
            }
          },
          error: (error) => {
            this.setErrorState(error);
          }
        })
      );
    } else {
      // If no enrollmentUuid, just get next step
      this.getNextStep();
    }
  }

  private getNextStep(forceForQuizCompletion: boolean = false): void {
    if (!this.enrollmentUuid) {
      console.error('Missing enrollmentUuid for getNextStep');
      this.mainIsLoading.set(false);
      return;
    }

    this.clearErrorState();
    if (!this.isPreamble()) {
      this.title.set('');
    }
    this.mainIsLoading.set(true);

    // Clean up interaction monitoring
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    if (
      this.realtimeService &&
      (this.isPreamble() || this.stepType === PathwayConstants.STEP_TYPE_INTRO)
    ) {
      try {
        this.realtimeService.endRealtimeConversation(true, true);
      } catch (error) {
        this.setErrorState(error as Error);
      }
    }

    this.subscriptions.push(
      this.pathwaysService.getNextStepByEnrollmentUuid(this.enrollmentUuid).subscribe({
        next: (response: IPathwayNextStepResponse) => {
          console.log('getNextStepByEnrollmentUuid response:', response);

          if (
            !forceForQuizCompletion &&
            this.stepType === PathwayStepType.QUIZ &&
            this.callStore?.getIsCallComplete()
          ) {
            return;
          }

          if (response.status === PathwayConstants.API_STATUS_NEXT_AVAILABLE) {
            // Next step is available, start it
            if (response.toStepUuid && response.stepType) {
              this.startStep(response);
            } else {
              this.setErrorState('Missing toStepUuid or stepType in NEXT_AVAILABLE response');
            }
          } else if (response.status === PathwayConstants.API_STATUS_COMPLETED) {
            // Pathway is completed, redirect to learning-pathway page
            this.navigateToPathwayHome();
            if (!this.moduleAssignmentUuid) {
              this.setErrorState('Missing moduleAssignmentUuid for redirect to pathway home page');
            }
          } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
            // Next step is blocked
            this.navigateToPathwayHome();
          } else if (response.status === PathwayConstants.API_STATUS_REDIRECTED) {
            // Step was redirected
            console.log('Step redirected:', {
              fromStepUuid: response.fromStepUuid,
              toStepUuid: response.toStepUuid,
              redirectReason: response.redirectReason
            });
            if (response.toStepUuid && response.stepType) {
              this.startStep(response);
            } else {
              this.setErrorState('Missing toStepUuid or stepType in REDIRECTED response');
            }
          } else {
            const errorMessage = `Unexpected getNextStep response status: ${response.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private setupPreambleStep(response: IPathwayRunStepResponse): void {
    if (response.stepName) {
      this.title.set(response.stepName);
    } else if (response.callName) {
      this.title.set(response.callName);
    }

    if (this.stepType === PathwayStepType.PRACTICE_CALL) {
      this.runStepResponse.set(response);
      this.callFlow.set('role-play');
      return;
    }

    // Reset call state
    this.callStore?.setIsCallComplete(false);
    this.callStore?.setDisplayPostCall(false);

    // Reset interaction counters
    if (this.controlService) {
      this.controlService.interactionCount = 0;
    }
    if (this.realtimeService) {
      this.realtimeService.avatarInteractionCount = 0;
      this.realtimeService.isFirstMessage = true;
    }

    // Store current step UUID from enrollmentStepUuid
    this.currentStepUuid = response.stepUuid || this.enrollmentStepUuid;

    // Transition to preamble state
    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');
    this.isPreamble.set(true);
    this.confirmActionLabel.set('learningPathway.BEGIN');
    this.disableConfirmActionButton.set(true);

    // Stop user video stream (no camera in preamble, except quiz preamble which shows camera)
    if (this.userVideoService && this.controlService?.isVideoOn && this.stepType !== PathwayStepType.QUIZ) {
      this.controlService.toggleVideo();
    }

    if (!response.callUuid) {
      console.error('Missing callUuid in preamble step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.enrollmentStepUuid = response.stepUuid;

    this.currentCallType = AppConstants.CALL_TYPE_FRAMING;

    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for preamble setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );

      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    // Start Tavus call for preamble
    this.startTavusCall(true);

    // Subscribe to welcome message completion for auto-completion
    this.isAutoCompleted = false;
    if (this.realtimeService) {
      this.subscriptions.push(
        this.realtimeService.welcomeMessageSpoken$.subscribe(() => {
          if (!this.isAutoCompleted && this.isPreamble()) {
            this.isAutoCompleted = true;
            this.completeStepOnly();
            this.disableConfirmActionButton.set(false);
          }
        })
      );
    }

    this.mainIsLoading.set(false);
  }

  private runStepAfterPreamble(stepUuid: string): void {
    // After preamble is completed, run the step again to get the main step instance
    if (!this.enrollmentUuid) {
      this.setErrorState('Missing enrollmentUuid for running step after preamble');
      return;
    }

    this.subscriptions.push(
      this.pathwaysService.runStepByEnrollmentUuid(this.enrollmentUuid, stepUuid).subscribe({
        next: (runResponse: IPathwayRunStepResponse) => {
          if (runResponse.status === PathwayConstants.API_STATUS_STARTED) {
            this.instanceUuid = runResponse.instanceUuid ?? '';
            // This should be the main step (not preamble)
            if (runResponse.isPreamble) {
              console.warn('Unexpected: Received preamble after preamble completion');
            }
            // Setup the main step
            this.setupNormalStep(runResponse);
          } else if (runResponse.status === PathwayConstants.API_STATUS_BLOCKED) {
            const errorMessage = runResponse.message || `Step execution blocked after preamble: ${runResponse.reason || 'Unknown reason'}`;
            this.setErrorState(errorMessage);
          } else if (runResponse.status === PathwayConstants.API_STATUS_ERROR) {
            const errorMessage = runResponse.message || `Error running step after preamble: ${runResponse.reason || 'Unknown error'}`;
            this.setErrorState(errorMessage);
          } else {
            const errorMessage = `Unexpected runStep response status after preamble: ${runResponse.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private startStep(response: IPathwayNextStepResponse): void {
    // Reset call state
    this.clearErrorState();
    this.avatarPhotoUrl.set('');
    this.showAvatarPhoto.set(false);
    this.callStore?.setIsCallComplete(false);
    this.callStore?.setDisplayPostCall(false);

    // Flow: get API → run API → show step
    // Use enrollmentStepUuid from step object (runStep API requires enrollment step UUID)
    if (!response.toStepUuid) {
      this.setErrorState('Missing toStepUuid in get API response for step');
      return;
    }

    if (!this.enrollmentUuid) {
      this.setErrorState('Missing enrollmentUuid for running step');
      return;
    }

    this.subscriptions.push(
      this.pathwaysService.runStepByEnrollmentUuid(this.enrollmentUuid, response.toStepUuid).subscribe({
        next: (runResponse: IPathwayRunStepResponse) => {
          if (runResponse.status === PathwayConstants.API_STATUS_STARTED) {
            this.instanceUuid = runResponse.instanceUuid ?? '';
            // Determine step type from the getNextStep response or component's stepType
            this.stepType = response.stepType as string;

            if (this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
              this.setupIntroStep(runResponse);
            } else {
              if (runResponse.isPreamble) {
                this.setupPreambleStep(runResponse);
              } else {
                this.setupNormalStep(runResponse);
              }
            }
          } else if (runResponse.status === PathwayConstants.API_STATUS_BLOCKED) {
            const errorMessage = runResponse.message || `Step execution blocked: ${runResponse.reason || 'Unknown reason'}`;
            this.setErrorState(errorMessage);
          } else if (runResponse.status === PathwayConstants.API_STATUS_ERROR) {
            const errorMessage = runResponse.message || `Error running step: ${runResponse.reason || 'Unknown error'}`;
            this.setErrorState(errorMessage);
          } else {
            const errorMessage = `Unexpected runStep response status: ${runResponse.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private runCurrentStepAfterPreamble(): void {
    if (!this.currentStepUuid) {
      this.setErrorState('Missing stepUuid for running step after preamble');
      return;
    }

    this.clearErrorState();
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    if (this.realtimeService) {
      try {
        this.realtimeService.endRealtimeConversation(true, true);
      } catch (error) {
        this.setErrorState(error as Error);
        return;
      }
    }

    this.mainIsLoading.set(true);
    this.runStepAfterPreamble(this.currentStepUuid);
  }

  private navigateToPathwayHome(): void {
    const moduleAssignmentUuid = this.moduleAssignmentUuid || this.route.snapshot.queryParams['moduleAssignmentUuid'];
    const practiceType = this.route.snapshot.queryParams['practiceType'] || PathwayConstants.PRACTICE_TYPE_PATHWAY;

    this.resetCallState();
    if (moduleAssignmentUuid) {
      this.router.navigate(['/learning-pathway'], {
        queryParams: {
          moduleAssignmentUuid: moduleAssignmentUuid,
          practiceType: practiceType
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.callStore?.setUserMicPreference(true);

    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      this.videoComponent?.pauseVideo();
    }

    if (this.realtimeService) {
      this.realtimeService.stopChromaKeying();
    }

    if (this.controlService?.intervalSpeechDetected) {
      clearInterval(this.controlService.intervalSpeechDetected);
    }

    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    this.pathwaySubtitleService.reset();

    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  // ─── Video progress tracking ──────────────────────────────────────────────

  /**
   * Called on every timeupdate tick from the video player.
   * Saves progress to the backend on two triggers:
   *  - A seek is detected (backward jump, or forward jump > 3 s)
   *  - The playhead crosses a VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL boundary (every 10 s).
   */
  protected onVideoTimeUpdate(event: { posMs: number; durationMs: number }): void {
    const { posMs, durationMs } = event;
    const prev = this.videoPositionMs;
    this.videoPositionMs = posMs;
    this.videoDurationMs = durationMs;

    // Seek detection: any backward jump or unusually large forward jump (> 2 s)
    const isSeek = posMs < prev || posMs > prev + 2000;
    if (isSeek && this.instanceUuid) {
      this.saveVideoProgress();
      return;
    }

    // Periodic checkpoint: save whenever the playhead crosses a 10 s boundary
    const prevCheckpoint = Math.floor(prev / this.VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL);
    const curCheckpoint = Math.floor(posMs / this.VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL);
    if (curCheckpoint > prevCheckpoint && this.instanceUuid) {
      this.saveVideoProgress();
    }
  }

  protected onVideoPlay(): void {
    this.isVideoCurrentlyPlaying.set(true);
  }

  /**
   * Called when the video player emits a pause event.
   * Saves the current position so the learner can resume from the exact spot.
   */
  protected onVideoPause(): void {
    this.isVideoCurrentlyPlaying.set(false);
    if (this.instanceUuid && this.videoPositionMs > 0) {
      this.saveVideoProgress();
    }
  }

  /**
   * Persists the current video position to the backend via updateInstanceProgress.
   */
  private saveVideoProgress(): void {
    const learnState: IVideoLearnState = {
      type: 'video',
      position_ms: this.videoPositionMs,
      duration_ms: this.videoDurationMs,
    };
    this.subscriptions.push(
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe()
    );
  }

  /**
   * Saves the current video position and immediately navigates away.
   * Called when the learner confirms leaving the step early via the leave-early modal.
   */
  private saveVideoProgressAndLeaveStep(): void {
    this.navigateAfterLeaveStep();
    if (this.instanceUuid && this.videoPositionMs > 0) {
      const learnState: IVideoLearnState = {
        type: 'video',
        position_ms: this.videoPositionMs,
        duration_ms: this.videoDurationMs,
      };
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe();
    }
  }

  /**
   * Resets the in-memory video position state.
   * Called at the start of each new video step.
   */
  private resetVideoProgress(): void {
    this.videoPositionMs = 0;
    this.videoDurationMs = 0;
    this.videoProgressInitialized = false;
  }

  private getInitialVideoPositionFromResponse(response: IPathwayRunStepResponse): number {
    const progress = response.instanceProgress;
    if (!progress || progress.type !== 'video') {
      return 0;
    }
    const videoProgress = progress as IVideoLearnState;
    if (typeof videoProgress.position_ms !== 'number' || videoProgress.position_ms <= 0) {
      return 0;
    }
    return videoProgress.position_ms;
  }


  /**
   * Initial instance_progress for a new video step instance.
   */
  protected onVideoReady(event: any): void {
    if (this.videoProgressInitialized || !this.instanceUuid) {
      return;
    }
    this.videoProgressInitialized = true;
    if (event?.durationMs) {
      this.initializeVideoInstanceProgress(event.durationMs);
    } else {
      this.videoProgressInitialized = false; // allow retry on next loadedmetadata
    }
  }

  private initializeVideoInstanceProgress(durationMs: number): void {
    const initialState: IVideoLearnState = {
      type: 'video',
      position_ms: this.videoStartTimeMs(),
      duration_ms: durationMs,
    };
    this.subscriptions.push(
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, initialState).subscribe()
    );
  }
}
